// Generate a downloadable PDF marketing brochure for Hireloop PRO.
// Visible only to super-admins from the AdminDashboard.

import { generatePdfFromHtml } from "./pdfUtil";

const LOGO_URL =
  "/brand/hireloop-h.png";

const STYLES = `
  .pdf-root { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Arial, sans-serif; color: #0f172a; background: #fff; line-height: 1.5; }
  .pdf-root .sheet { width: 8.5in; height: 11in; position: relative; overflow: hidden; page-break-after: always; }
  .pdf-root .sheet.last { page-break-after: auto; }

  /* COVER */
  .pdf-root .cover { background: linear-gradient(140deg, #0f172a 0%, #1d4ed8 55%, #38bdf8 110%); color: #fff; padding: 56px 52px; height: 100%; display: flex; flex-direction: column; justify-content: space-between; box-sizing: border-box; }
  .pdf-root .cover .top { display: flex; align-items: center; gap: 14px; }
  .pdf-root .cover .top img { width: 52px; height: 52px; filter: brightness(0) invert(1); }
  .pdf-root .cover .top .wordmark { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: #fff; }
  .pdf-root .cover .top .wordmark span { color: #7dd3fc; }
  .pdf-root .cover h1 { font-size: 56px; line-height: 1.02; letter-spacing: -0.03em; font-weight: 800; margin: 0 0 18px; color: #fff; }
  .pdf-root .cover .lede { font-size: 17px; line-height: 1.55; max-width: 540px; color: rgba(255,255,255,0.88); margin-bottom: 28px; }
  .pdf-root .cover .pillars { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; max-width: 560px; }
  .pdf-root .cover .pillar { border: 1px solid rgba(255,255,255,0.18); padding: 14px 16px; background: rgba(255,255,255,0.06); }
  .pdf-root .cover .pillar .num { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #7dd3fc; }
  .pdf-root .cover .pillar .label { font-size: 15px; font-weight: 600; margin-top: 4px; color: #fff; }
  .pdf-root .cover .cover-footer { display: flex; justify-content: space-between; font-size: 11px; color: rgba(255,255,255,0.7); letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600; }

  /* INSIDE PAGES */
  .pdf-root .page { padding: 48px 52px 56px; height: 100%; box-sizing: border-box; }
  .pdf-root .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #1d4ed8; }
  .pdf-root .brand-rule { height: 3px; width: 64px; margin: 8px 0 22px; background: linear-gradient(90deg, #1d4ed8 0%, #2563eb 35%, #38bdf8 100%); }
  .pdf-root h2 { font-size: 28px; line-height: 1.15; letter-spacing: -0.02em; font-weight: 700; margin: 0 0 12px; color: #0f172a; }
  .pdf-root .lede { font-size: 14px; line-height: 1.6; color: #475569; max-width: 600px; margin-bottom: 22px; }
  .pdf-root .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0; border: 1px solid #e2e8f0; }
  .pdf-root .grid .cell { padding: 18px 22px; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; box-sizing: border-box; }
  .pdf-root .grid .cell:nth-child(2n) { border-right: 0; }
  .pdf-root .grid .cell:nth-last-child(-n+2) { border-bottom: 0; }
  .pdf-root .cell .num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; font-weight: 700; color: #1d4ed8; letter-spacing: 0.18em; }
  .pdf-root .cell h3 { font-size: 15px; font-weight: 700; margin: 6px 0 6px; color: #0f172a; }
  .pdf-root .cell p { font-size: 12.5px; line-height: 1.55; color: #475569; margin: 0; }

  .pdf-root .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin: 22px 0 0; }
  .pdf-root .stat { padding: 18px 0; }
  .pdf-root .stat .v { font-size: 34px; font-weight: 800; letter-spacing: -0.02em; color: #1d4ed8; }
  .pdf-root .stat .l { font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #64748b; margin-top: 6px; }

  .pdf-root .pricing { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px; }
  .pdf-root .plan { border: 1px solid #e2e8f0; padding: 20px 18px; display: flex; flex-direction: column; gap: 10px; box-sizing: border-box; }
  .pdf-root .plan.high { border-color: #1d4ed8; box-shadow: 0 0 0 2px rgba(29, 78, 216, 0.08); }
  .pdf-root .plan .ribbon { font-size: 10px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #1d4ed8; }
  .pdf-root .plan .name { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: #0f172a; }
  .pdf-root .plan .price { font-size: 28px; font-weight: 800; letter-spacing: -0.02em; color: #0f172a; }
  .pdf-root .plan .price small { font-size: 12px; color: #64748b; font-weight: 500; }
  .pdf-root .plan ul { margin: 0; padding: 0; list-style: none; }
  .pdf-root .plan li { font-size: 12.5px; padding-left: 16px; position: relative; margin: 3px 0; color: #1e293b; }
  .pdf-root .plan li::before { content: "✓"; position: absolute; left: 0; color: #047857; font-weight: 700; }

  /* CTA / BACK */
  .pdf-root .cta-sheet { background: #0f172a; color: #fff; padding: 72px 52px 56px; height: 100%; display: flex; flex-direction: column; justify-content: space-between; box-sizing: border-box; }
  .pdf-root .cta-sheet .eyebrow { color: #7dd3fc; }
  .pdf-root .cta-sheet .brand-rule { background: linear-gradient(90deg, #7dd3fc 0%, #38bdf8 100%); }
  .pdf-root .cta-sheet h2 { font-size: 44px; line-height: 1.05; max-width: 640px; color: #fff; margin-bottom: 16px; }
  .pdf-root .cta-sheet .lede { color: rgba(255,255,255,0.82); max-width: 540px; font-size: 16px; }
  .pdf-root .cta-box { margin-top: 28px; padding: 22px 26px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16); display: flex; gap: 28px; align-items: center; justify-content: space-between; flex-wrap: wrap; }
  .pdf-root .cta-box .label { font-size: 11px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #7dd3fc; }
  .pdf-root .cta-box .url { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; color: #fff; }
  .pdf-root .cta-box .arrow { font-size: 28px; color: #7dd3fc; }
  .pdf-root .cta-sheet .cta-footer { font-size: 11px; color: rgba(255,255,255,0.6); letter-spacing: 0.22em; text-transform: uppercase; font-weight: 600; display: flex; justify-content: space-between; align-items: center; }
  .pdf-root .cta-sheet .cta-footer img { width: 32px; height: 32px; filter: brightness(0) invert(1); opacity: 0.6; }
`;

const BODY = `
  <!-- COVER -->
  <section class="sheet">
    <div class="cover">
      <div class="top">
        <img src="${LOGO_URL}" alt="Hireloop PRO" crossorigin="anonymous" />
        <div class="wordmark">Hireloop <span>PRO</span></div>
      </div>
      <div>
        <h1>Your next hire is already on tape.</h1>
        <p class="lede">Hireloop PRO automates the entire screening loop — AI-generated questions, async video interviews, live 1:1 calls, résumé scoring, automated reminders. You spend your time on the final five candidates, not the first fifty.</p>
        <div class="pillars">
          <div class="pillar"><div class="num">01</div><div class="label">AI-graded video interviews</div></div>
          <div class="pillar"><div class="num">02</div><div class="label">Résumé ranking + online form</div></div>
          <div class="pillar"><div class="num">03</div><div class="label">Unified applicants pipeline</div></div>
          <div class="pillar"><div class="num">04</div><div class="label">Monthly or annual · Save 10%</div></div>
        </div>
      </div>
      <div class="cover-footer">
        <span>Marketing brochure</span>
        <span>hireloop.pro</span>
      </div>
    </div>
  </section>

  <!-- WHY -->
  <section class="sheet">
    <div class="page">
      <div class="eyebrow">Why Hireloop PRO</div>
      <div class="brand-rule"></div>
      <h2>Stop watching 40 videos to find one hire.</h2>
      <p class="lede">Talent teams lose 11 hours a week on first-round screens. Our AI watches every interview for you, scores each answer on a 0–10 scale with reasoning, and surfaces the candidates that actually deserve a callback. No more inbox archaeology. No more Friday-afternoon panel reviews.</p>

      <div class="grid">
        <div class="cell"><div class="num">01</div><h3>AI-generated questions</h3><p>Type the job title — get a tailored set of behavioural and technical questions. Regenerate or hand-edit any one of them.</p></div>
        <div class="cell"><div class="num">02</div><h3>Async video interviews</h3><p>Candidates record from any browser, two-minute cap per answer. No scheduling. No timezone tetris. No installs.</p></div>
        <div class="cell"><div class="num">03</div><h3>Live 1:1 camera rooms</h3><p>Spin up a WebRTC room and share the link — no Zoom accounts, no calendar bloat. Use it for finals or quick chats.</p></div>
        <div class="cell"><div class="num">04</div><h3>Whisper-grade transcripts</h3><p>Every answer is transcribed and searchable. Share a candidate's transcript with the hiring panel in one click.</p></div>
        <div class="cell"><div class="num">05</div><h3>Objective scoring</h3><p>0–10 with reasoning for every answer, plus a quality preview so candidates can self-correct before they submit.</p></div>
        <div class="cell"><div class="num">06</div><h3>Résumé ranking + form path</h3><p>Drag PDFs in OR let candidates fill an online application form — both flow through the same AI scoring pipeline.</p></div>
      </div>

      <div class="stats">
        <div class="stat"><div class="v">11h</div><div class="l">saved per week</div></div>
        <div class="stat"><div class="v">3×</div><div class="l">faster shortlist</div></div>
        <div class="stat"><div class="v">10%</div><div class="l">off with annual</div></div>
      </div>
    </div>
  </section>

  <!-- PLATFORM + PRICING -->
  <section class="sheet">
    <div class="page">
      <div class="eyebrow">Platform overview</div>
      <div class="brand-rule"></div>
      <h2>One workspace. The full hiring loop.</h2>
      <p class="lede">Beyond interviews — every candidate across every job lives in a unified Applicants pipeline with auto-assigned <strong>APP-YYYY-NNNNN</strong> reference numbers, mm/dd/yyyy received dates, and a 6-stage status dropdown. Candidates can self-serve their pipeline status at a branded URL you can iframe on your own site. Auto-flipping to "Awaiting Response" the moment you invite, optional branded rejection / withdrawal emails (editable templates), and bulk-delete clean-up keep the funnel tidy without manual book-keeping.</p>

      <div class="grid">
        <div class="cell"><div class="num">07</div><h3>One-click outreach</h3><p>Invite your top N résumés in a single action — emails delivered via Resend with open/click tracking and automated polite reminders.</p></div>
        <div class="cell"><div class="num">08</div><h3>Embed careers + status</h3><p>Two paste-ready iframes: a public careers page (AI-score every résumé) and a candidate status check (ref# + email → pipeline stage).</p></div>
        <div class="cell"><div class="num">09</div><h3>Branded candidate emails</h3><p>Optional polite emails on Not Hired / Not Interested. Editable per-employer templates with tokens, plus Preview + Send-test-to-me.</p></div>
        <div class="cell"><div class="num">10</div><h3>Secure by default</h3><p>JWT auth with rotation on password change, rate-limited public status lookups (anti-enumeration), Stripe-managed billing.</p></div>
      </div>

      <h2 style="margin-top: 36px; font-size: 24px;">Plans that scale — monthly or annual (save 10%)</h2>
      <div class="pricing">
        <div class="plan">
          <div class="ribbon">Starter</div>
          <div class="name">Starter</div>
          <div class="price">$39.00<small>/mo</small></div>
          <ul>
            <li>10 interviews / month</li>
            <li>AI transcription &amp; scoring</li>
            <li>Email support</li>
          </ul>
        </div>
        <div class="plan high">
          <div class="ribbon">Most popular</div>
          <div class="name">Pro</div>
          <div class="price">$99.00<small>/mo</small></div>
          <ul>
            <li>50 interviews / month</li>
            <li>Résumé ranking &amp; exports</li>
            <li>Automated reminders</li>
            <li>Priority support</li>
          </ul>
        </div>
        <div class="plan">
          <div class="ribbon">Enterprise</div>
          <div class="name">Enterprise</div>
          <div class="price">$299.00<small>/mo</small></div>
          <ul>
            <li>Unlimited interviews</li>
            <li>ATS push (Greenhouse / Lever)</li>
            <li>Custom branding &amp; SLA</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA / BACK -->
  <section class="sheet last">
    <div class="cta-sheet">
      <div>
        <div class="eyebrow">Get started</div>
        <div class="brand-rule"></div>
        <h2>Start ranking candidates today.</h2>
        <p class="lede">Create your first AI interview in under two minutes. Cards on file? No need — every plan starts on a 10-day trial, and you can comp internal accounts from the admin portal.</p>
        <div class="cta-box">
          <div>
            <div class="label">Visit</div>
            <div class="url">hireloop.pro</div>
          </div>
          <div class="arrow">→</div>
          <div>
            <div class="label">Or email</div>
            <div class="url">sales@hireloop.ai</div>
          </div>
        </div>
      </div>
      <div class="cta-footer">
        <span>© Hireloop PRO ${new Date().getFullYear()}</span>
        <img src="${LOGO_URL}" alt="Hireloop PRO" crossorigin="anonymous" />
        <span>AI camera-to-camera interviews</span>
      </div>
    </div>
  </section>
`;

export async function openMarketingBrochure() {
  await generatePdfFromHtml({
    filename: "Hireloop-Marketing-Brochure.pdf",
    styles: STYLES,
    body: BODY,
  });
  return true;
}
